import React from "react";
import { ShieldCheck, FlaskConical, Truck, Lock, PackageCheck, BadgeCheck } from "lucide-react";

/**
 * Homepage trust section — six brand-aligned trust signals rendered in a
 * responsive grid. Placed high on the landing page to establish credibility
 * before the featured products fold, and to give crawlers/AI scanners
 * extractable trust signals (COA, HPLC, US shipping, secure checkout, 21+,
 * discreet packaging) that boost topical authority.
 */
const TRUST_SIGNALS = [
  {
    icon: FlaskConical,
    label: "Third-Party HPLC Verified",
    body: "Every batch is independently tested by ISO-certified labs. Certificate of Analysis available on request.",
    testId: "trust-hplc",
  },
  {
    icon: BadgeCheck,
    label: "≥99% Purity Standard",
    body: "Research-grade peptides held to a strict 99% or higher purity benchmark across every SKU we stock.",
    testId: "trust-purity",
  },
  {
    icon: Truck,
    label: "US Domestic · Southern CA",
    body: "Ships same-day from Southern California in cold-chain-friendly packaging. Tracked delivery on every order.",
    testId: "trust-shipping",
  },
  {
    icon: PackageCheck,
    label: "Discreet Packaging",
    body: "Unbranded outer packaging — no peptide references on the label. Signature-optional at checkout.",
    testId: "trust-discreet",
  },
  {
    icon: Lock,
    label: "Secure Checkout",
    body: "Encrypted payment processing with fraud protection. Order data never sold, ever.",
    testId: "trust-secure",
  },
  {
    icon: ShieldCheck,
    label: "21+ Research Compliance",
    body: "Verified 21+ age gate and research-use-only agreement on every order. Compliant with US federal guidelines.",
    testId: "trust-compliance",
  },
];

export default function TrustSection() {
  return (
    <section
      className="max-w-7xl mx-auto px-6 py-20 border-t border-white/5"
      data-testid="homepage-trust-section"
    >
      <div className="mb-12">
        <div className="overline">WHY RESEARCHERS TRUST PSL</div>
        <h2 className="font-heading text-3xl sm:text-4xl mt-3 font-light max-w-2xl">
          Lab-verified quality, discreet delivery, backed by the numbers.
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {TRUST_SIGNALS.map((s) => (
          <div
            key={s.label}
            className="bg-[#0C0C12] p-8 flex flex-col gap-4 hover:bg-[#12121A] transition-colors"
            data-testid={s.testId}
          >
            <div className="w-11 h-11 rounded-full border border-[#00C3FF]/30 bg-[#00C3FF]/5 flex items-center justify-center">
              <s.icon className="text-[#00C3FF]" size={18} />
            </div>
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-white">
                {s.label}
              </div>
              <p className="text-sm text-[#8A94A6] mt-3 leading-relaxed">{s.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
